import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useFollow } from '../utils/FollowContext';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import { FaUserCheck, FaUserPlus, FaTrash } from 'react-icons/fa';

export default function NotificationItem({ notification }) {
  const auth = getAuth();
  const navigate = useNavigate();
  const { isFollowing, shouldFollowBack, toggleFollow, isInitialized } = useFollow();
  const [senderData, setSenderData] = useState({
    name: 'Unknown',
    profileImage: defaultAvatar
  });
  const [statusData, setStatusData] = useState(null);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showDeleteDropdown, setShowDeleteDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef(null);

  // Fetch sender's data (name, profile image)
  useEffect(() => {
    async function fetchSenderData() {
      try {
        const userRef = doc(db, 'users', notification.senderId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setSenderData({
            name: data.name || 'Unknown',
            profileImage: data.photoURL || defaultAvatar
          });
        } else {
          setError('Sender data not found');
        }
      } catch (error) {
        console.error('Error fetching sender data:', error);
        setError('Failed to load sender data');
      }
    }

    async function fetchData() {
      setIsLoading(true);
      await fetchSenderData();
      setIsLoading(false);
    }

    if (notification.senderId) {
      fetchData();
    } else {
      setError('Invalid sender ID');
      setIsLoading(false);
    }
  }, [notification.senderId]);

  // Fetch status data for Like, Comment, Save notifications
  useEffect(() => {
    if (notification.type !== 'follow' && notification.targetId) {
      async function fetchStatusData() {
        try {
          const statusRef = doc(db, 'status', notification.targetId);
          const statusSnap = await getDoc(statusRef);
          if (statusSnap.exists()) {
            setStatusData(statusSnap.data());
          } else {
            setError('Status not found');
          }
        } catch (error) {
          console.error('Error fetching status data:', error);
          setError('Failed to load status data');
        }
      }
      fetchStatusData();
    }
  }, [notification.type, notification.targetId]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDeleteDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate time since notification
  const timeSinceNotification = () => {
    if (!notification.timestamp || typeof notification.timestamp.toDate !== 'function') {
      return 'Just now'; // Fallback for invalid timestamps
    }

    const notifTime = notification.timestamp.toDate();
    const now = new Date();
    const diffSeconds = Math.floor((now - notifTime) / 1000);

    if (diffSeconds < 60) return 'Just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h`;
    if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d`;
    return new Date(notifTime).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
  };

  // Handle follow/unfollow (for Follow notifications)
  const handleFollowBack = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      await toggleFollow(notification.senderId);
    } catch (error) {
      console.error('Error handling follow back:', error);
      setError('Failed to follow/unfollow');
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Determine follow button style and icon
  const getFollowButtonState = () => {
    if (isFollowing(notification.senderId)) {
      return {
        className: 'bg-black hover:bg-gray-800',
        icon: <FaUserCheck size={16} />,
        text: 'Following'
      };
    }
    if (shouldFollowBack(notification.senderId)) {
      return {
        className: 'bg-blue-500 hover:bg-blue-600',
        icon: <FaUserPlus size={22} />,
        text: 'Follow Back'
      };
    }
    return {
      className: 'bg-blue-500 hover:bg-blue-600',
      icon: <FaUserPlus size={22} />,
      text: 'Follow Back'
    };
  };

  const followState = getFollowButtonState();

  // Handle navigation for the column and content container
  const handleContentTap = (e) => {
    e.stopPropagation(); // Prevent profile image click from interfering
    if (notification.type === 'follow') {
      if (notification.senderId) {
        navigate(`/profile/${notification.senderId}`);
      }
    } else if (notification.targetId) {
      navigate(`/status/${notification.targetId}/full`);
    }
  };

  // Handle navigation for the profile image
  const handleProfileImageTap = (e) => {
    e.stopPropagation(); // Prevent content tap from interfering
    if (notification.senderId) {
      navigate(`/profile/${notification.senderId}`);
    }
  };

  // Handle long press to delete notification (mobile)
  const handleLongPressStart = (e) => {
    e.preventDefault(); // Prevent default touch behavior
    const timer = setTimeout(() => {
      if (window.confirm('Are you sure you want to delete this notification?')) {
        handleDelete();
      }
    }, 500); // 500ms for long press
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Handle right-click to show delete dropdown (desktop)
  const handleRightClick = (e) => {
    e.preventDefault(); // Prevent default context menu
    setShowDeleteDropdown(true);
    setDropdownPosition({
      x: e.clientX,
      y: e.clientY
    });
  };

  // Delete the notification from Firestore
  const handleDelete = async () => {
    try {
      const notifRef = doc(db, `notifications/${auth.currentUser.uid}/userNotifications`, notification.id);
      await deleteDoc(notifRef);
      setShowDeleteDropdown(false);
      // No need to update state here since the parent Notifications component uses onSnapshot
    } catch (error) {
      console.error('Error deleting notification:', error);
      setError('Failed to delete notification');
    }
  };

  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center px-4 py-3 bg-black border-b border-gray-800">
        <div className="w-12 h-12 rounded-full bg-gray-700 animate-pulse"></div>
        <div className="flex-1 ml-3">
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-2 animate-pulse"></div>
          <div className="h-4 bg-gray-700 rounded w-3/4 animate-pulse"></div>
        </div>
        <div className="w-[60px] h-[60px] bg-gray-700 rounded-lg animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center px-4 py-3 bg-black border-b border-gray-800">
        <div className="text-gray-400 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center px-4 py-3 bg-black border-b border-gray-800 hover:bg-gray-900 cursor-pointer relative"
      onTouchStart={handleLongPressStart}
      onTouchEnd={handleLongPressEnd}
      onTouchCancel={handleLongPressEnd}
      onContextMenu={handleRightClick}
    >
      {/* Profile Image */}
      <div className="flex-shrink-0">
        <img
          src={senderData.profileImage}
          alt={`${senderData.name}'s profile`}
          className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
          onError={(e) => (e.target.src = defaultAvatar)}
          onClick={handleProfileImageTap}
        />
      </div>

      {/* Text Column */}
      <div 
        className="flex-1 ml-3"
        onClick={handleContentTap}
      >
        <p className="text-white font-bold text-sm">{senderData.name}</p>
        <p className="text-white text-sm line-clamp-2">{notification.message}</p>
        <p className="text-gray-400 text-xs mt-1">{timeSinceNotification()}</p>
      </div>

      {/* Square Container */}
      <div 
        className="flex-shrink-0 ml-3"
        onClick={handleContentTap}
      >
        {notification.type === 'follow' ? (
          // Follow Back Button
          <button
            onClick={handleFollowBack}
            disabled={isFollowLoading}
            className={`w-20 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium transition duration-300 focus:outline-none ${followState.className} ${isFollowLoading ? 'opacity-70' : ''}`}
          >
            {isFollowLoading ? '...' : (
              <>
                {followState.icon}
                <span className="ml-1">{followState.text}</span>
              </>
            )}
          </button>
        ) : (
          // Status Content Preview
          <div className="w-[60px] h-[60px] bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
            {statusData?.imageUrls?.length > 0 ? (
              <img
                src={statusData.imageUrls[0]}
                alt="Status preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <p className="text-white text-xs p-2 line-clamp-3">
                {statusData?.text || 'No content'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Delete Dropdown for Desktop */}
      {showDeleteDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-20 bg-gray-800 border border-gray-600 rounded-md shadow-lg"
          style={{
            top: `${dropdownPosition.y}px`,
            left: `${dropdownPosition.x}px`,
            transform: 'translate(-50%, 0)',
            minWidth: '120px'
          }}
        >
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this notification?')) {
                handleDelete();
              }
            }}
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
          >
            <FaTrash size={14} />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}